import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tool, UserData } from '../types';
import { ArrowLeft, Pencil, Loader2, PlayCircle, Info, Settings, Code, Plus, X, Eye, Sparkles } from 'lucide-react';
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/TextArea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";
import { MultiSelect } from "../components/ui/MultiSelect";
import { useToast } from "../components/ui/Toast";
import { TagManagement, TagManagementRef } from "../components/TagManagement";
import { FormField } from '../components/FormField';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { toolFormHelpText, formatHelpText } from '../config/helptext-config';
import { ADMIN_SERVICE_USER, ANNOTATION_URL, DEFAULT_TOOL_TIMEOUT } from '../config/api-config';
import { AccessControlSection } from '../components/AccessControlSection';
import { useAuth } from '../contexts/AuthContext';
import { useUsersApi } from '../apis/users-apis';
import AccessDisplay from '../components/AccessDisplay';
import OwnerSelection from '../components/OwnerSelection';
import { createApiHeadersGetter } from '../utils/api-headers';
import { Checkbox } from '../components/ui/Checkbox';

interface AccessControl {
  editAccessType: string;
  editAllowedUsers: string[];
  viewAccessType: string;
  viewAllowedUsers: string[];
}

// View Mode Components
const ViewModeField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="mb-6">
    <h3 className="text-sm font-medium text-gray-500 mb-2">{label}</h3>
    {typeof value === 'string' ? (
      <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap border border-gray-200">
        {value || 'No content provided'}
      </div>
    ) : (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        {value}
      </div>
    )}
  </div>
);

interface Parameter {
  key: string;
  description: string;
  type: string;
  options?: string;
  example?: string;
}

const ViewModeParameters: React.FC<{ parameters: Parameter[] }> = ({ parameters = [] }) => {
  if (!Array.isArray(parameters) || parameters.length === 0) {
    return <div className="text-gray-500 italic">No parameters configured</div>;
  }

  return (
    <div className="space-y-4">
      {parameters.map((param, index) => (
        <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-start">
            <div className="flex-grow">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{param.key}</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  {param.type}
                </span>
              </div>
              <p className="text-gray-600 mt-1">{param.description}</p>
              {(param.options || param.example) && (
                <div className="mt-2 space-y-1 text-sm">
                  {param.options && (
                    <div className="text-gray-600">
                      <span className="font-medium">Options:</span> {param.options}
                    </div>
                  )}
                  {param.example && (
                    <div className="text-gray-600">
                      <span className="font-medium">Example:</span> {param.example}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const ViewModeOutput: React.FC<{ output: any }> = ({ output }) => {
  if (!output || !output.parameters || !output.parameters.output) {
    return <div className="text-gray-500 italic">No output configured</div>;
  }

  const outputData = output.parameters.output;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-start">
        <div className="flex-grow">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">output</span>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
              {outputData.type}
            </span>
          </div>
          <p className="text-gray-600 mt-1">{outputData.description}</p>
        </div>
      </div>
    </div>
  );
};

const ViewModeAnnotation: React.FC<{ annotation?: string }> = ({ annotation }) => {
  if (!annotation) {
    return <div className="text-gray-500 italic">No annotation available</div>;
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap">
      {annotation}
    </div>
  );
};
interface ToolFormProps {
  tool: Tool;
  originalTool?: Tool | null;
  onToolChange: (tool: Tool) => void;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
  readOnly?: boolean;
  onEditClick?: () => void;
  onDiscardChanges?: () => void;
  hasUnsavedChanges?: boolean;
  setIsReadOnly: (value: boolean) => void;
  tagSuggestions?: string[];
  existingToolNames?: string[];
  currentUserId: string;
  onOwnerChange?: (newOwnerId: string) => Promise<void>;
  isSaving?: boolean;
}

export interface ToolFormRef {
  validateForm: () => Promise<boolean>;
  getErrors: () => Record<string, string>;
}

const LoadingOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      <span className="text-lg font-medium">Generating Annotation... Please wait</span>
    </div>
  </div>
);

const SaveOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      <span className="text-lg font-medium">Saving Tool... Please wait</span>
    </div>
  </div>
);

interface UserAPIResponse {
  users: {
    userId: string;
    email: string;
    isAdmin: boolean;
    isActive: boolean;
  }[];
}

  const ToolForm = forwardRef<ToolFormRef, ToolFormProps>((props, ref) => {
    const {
      tool,
      onToolChange,
      onSubmit,
      onCancel,
      isEditing = false,
      readOnly = false,
      onEditClick,
      onDiscardChanges,
      tagSuggestions = []
    } = props;

    const { user } = useAuth();
    const navigate = useNavigate();

  const { addToast } = useToast();
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isAuthFieldTouched, setIsAuthFieldTouched] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [isAuthEnabled, setIsAuthEnabled] = useState(tool.isAuthPresent || false);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [parameterToDelete, setParameterToDelete] = useState<number | null>(null);
  const [isAddingParameter, setIsAddingParameter] = useState(false);
  const tagManagementRef = useRef<TagManagementRef>(null);
  const [isCoreTool, setIsCoreTool] = useState(tool.isCore || false);
  const [adminServiceUserId, setAdminServiceUserId] = useState<string>('');
  const totalSteps = isCoreTool ? 5 : 6; // Core tools: 5 steps, Regular tools: 6 steps

  // Parameter Management State
  const [newParamKey, setNewParamKey] = useState("");
  const [newParamDesc, setNewParamDesc] = useState("");
  const [newParamType, setNewParamType] = useState("str");
  const [newParamOptions, setNewParamOptions] = useState("");
  const [newParamExample, setNewParamExample] = useState("");

  const [editingParam, setEditingParam] = useState<number | null>(null);
  const [editParamKey, setEditParamKey] = useState("");
  const [editParamDesc, setEditParamDesc] = useState("");
  const [editParamType, setEditParamType] = useState("str");
  const [editParamOptions, setEditParamOptions] = useState("");
  const [editParamExample, setEditParamExample] = useState("");
  const [editErrors, setEditErrors] = useState<{
    key?: string;
    description?: string;
  }>({});

  const [isAnnotationEditing, setIsAnnotationEditing] = useState(false);
  const [tempAnnotation, setTempAnnotation] = useState('');
  const [isGeneratingAnnotation, setIsGeneratingAnnotation] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const { getUsers } = useUsersApi();
  const { getAuthToken } = useAuth();
  const getHeaders = createApiHeadersGetter(getAuthToken);

  // Annotation generation function
  const generateAnnotation = async () => {
    if (!tool.toolName || !tool.toolDescription || !tool.toolUrlEndpoint) {
      addToast({
        title: "Missing Information",
        description: "Please fill in tool name, description, and API endpoint before generating annotation",
        variant: "destructive",
        duration: 4000
      });
      return;
    }

    setIsGeneratingAnnotation(true);
    try {
      const headers = await getHeaders();
      const response = await fetch(ANNOTATION_URL, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          toolName: tool.toolName,
          toolDescription: tool.toolDescription,
          toolUrlEndpoint: tool.toolUrlEndpoint,
          toolParameters: tool.toolParameters,
          toolOutput: tool.toolOutput
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.aiAnnotation) {
        handleInputChange('toolAnnotation', data.aiAnnotation);
        addToast({
          title: "Success",
          description: "Annotation generated successfully",
          variant: "default",
          duration: 3000
        });
      } else {
        throw new Error('No annotation returned from API');
      }
    } catch (error) {
      console.error('Error generating annotation:', error);
      addToast({
        title: "Error",
        description: "Failed to generate annotation. Please try again.",
        variant: "destructive",
        duration: 4000
      });
    } finally {
      setIsGeneratingAnnotation(false);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const usersData = await getUsers();
        const activeUsers = usersData.filter(user => user.isActive);
        setUsers(activeUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
        addToast({
          title: "Error",
          description: "Failed to fetch users list",
          variant: "destructive",
          duration: 4000
        });
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchAdminStatus = async () => {
      if (user?.sub) {
        try {
          const usersData = await getUsers();
          const userData = usersData.find((u) => u.userId === user.sub);
          setIsCurrentUserAdmin(userData?.isAdmin || false);
          
          // Find the admin service account ID
          const adminServiceAccount = usersData.find(u => 
            u.email === ADMIN_SERVICE_USER
          );
          
          if (adminServiceAccount) {
            setAdminServiceUserId(adminServiceAccount.userId);
          } else {
            console.warn(`Admin service account ${ADMIN_SERVICE_USER} not found in users list`);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setIsCurrentUserAdmin(false);
        }
      }
    };
  
    fetchAdminStatus();
  }, [user?.sub]);

  useEffect(() => {
    // When isCoreTool is enabled, set default access control to "Owner Only"
    // and set owner to admin service account
    if (isCoreTool) {
      onToolChange({
        ...tool,
        isCore: true,
        ownerId: adminServiceUserId || tool.ownerId,
        accessControl: {
          viewAccessType: 'Owner Only',
          viewAllowedUsers: [],
          editAccessType: 'Owner Only',
          editAllowedUsers: []
        }
      });
    } else {
      onToolChange({
        ...tool,
        isCore: false
      });
    }
  }, [isCoreTool, adminServiceUserId]);

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!tool.toolName?.trim()) {
      newErrors.toolName = 'Tool name is required';
    } else {
      const validation = validateToolName(tool.toolName, props.existingToolNames || [], tool.toolId);
      if (!validation.isValid) {
        newErrors.toolName = validation.error;
      }
    }
    
    if (!tool.toolDescription?.trim()) {
      newErrors.toolDescription = 'Description is required';
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!tool.toolUrlEndpoint?.trim()) {
      newErrors.toolUrlEndpoint = 'API Endpoint is required';
    }
    
    if (!tool.toolTimeout || Number(tool.toolTimeout) <= 0) {
      newErrors.toolTimeout = 'Timeout must be greater than 0';
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = (): boolean => {
    // Step 3 (parameters) is optional, so always valid
    return true;
  };

  const validateStep4 = (): boolean => {
    // Step 4: Output - validate output parameters
    const newErrors: Record<string, string> = {};
    
    if (!tool.toolOutput?.parameters?.output) {
      newErrors.output = 'Output is required';
    } else if (!tool.toolOutput.parameters.output.description?.trim()) {
      newErrors.output = 'Output description is required';
    } else if (!tool.toolOutput.parameters.output.type) {
      newErrors.output = 'Output type is required';
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const validateStep5 = (): boolean => {
    // Step 5: Annotation - validate annotation
    const newErrors: Record<string, string> = {};
    
    if (!tool.toolAnnotation?.trim()) {
      newErrors.toolAnnotation = 'Annotation is required';
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const validateStep6 = (): boolean => {
    // Step 6: Access Control (only for non-core tools)
    if (isCoreTool) return true;
    
    const newErrors: Record<string, string> = {};
    
    if (!tool.accessControl.viewAccessType) {
      newErrors.viewAccessType = 'View Access Type is required';
    }
    
    if (!tool.accessControl.editAccessType) {
      newErrors.editAccessType = 'Edit Access Type is required';
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    let isValid = false;
    
    switch (currentStep) {
      case 1:
        isValid = validateStep1();
        break;
      case 2:
        isValid = validateStep2();
        break;
      case 3:
        isValid = validateStep3();
        break;
      case 4:
        isValid = validateStep4();
        break;
      case 5:
        isValid = validateStep5();
        break;
      case 6:
        isValid = validateStep6();
        break;
    }
    
    if (isValid) {
      setCompletedSteps(prev => {
        const newSet = new Set(prev);
        newSet.add(currentStep);
        return newSet;
      });
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (step: number) => {
    if (step <= currentStep || completedSteps.has(step - 1)) {
      setCurrentStep(step);
    }
  };

  const validateToolName = (name: string, existingNames: string[], currentToolId?: string): { isValid: boolean; error: string } => {
    if (!name.trim()) {
      return { isValid: false, error: 'Tool Name is required' };
    }

    if (name.length > 63) {
      return { isValid: false, error: 'Tool Name must be less than 64 characters' };
    }
    
    // Check for disallowed characters (hyphens and spaces)
    if (name.includes('-') || name.includes(' ')) {
      return { 
        isValid: false, 
        error: 'Tool Name can only contain letters, numbers, and underscores. Hyphens and spaces are not allowed'
      };
    }

    // Check for uppercase letters
    if (/[A-Z]/.test(name)) {
      return {
        isValid: false,
        error: 'Tool Name must be in lowercase'
      };
    }
  
    // Only allow letters, numbers, and underscores
    const validNameRegex = /^[a-z0-9_]+$/;
    if (!validNameRegex.test(name)) {
      return { 
        isValid: false, 
        error: 'Tool Name can only contain lower case letters, numbers, and underscores'
      };
    }

    // Check for uniqueness - only if we have existing names
    if (existingNames.length > 0) {
      const isDuplicate = existingNames.some(existingName => 
        existingName.toLowerCase() === name.toLowerCase() && 
        (!currentToolId || currentToolId !== existingName) // Exclude current tool when editing
      );
      
      if (isDuplicate) {
        return {
          isValid: false,
          error: 'A Tool with this name already exists. Please choose a different name.'
        };
      }
    }
  
    return { isValid: true, error: '' }; // Always include error property
  };

  const validateForm = async (): Promise<boolean> => {
    if (readOnly) return true;
    
    const newErrors: Record<string, string> = {};
  
    // Ensure tool object exists
    if (!tool) {
      return false;
    }
  
    // Validate tool name
    if (!tool.toolName) {
      newErrors.toolName = 'Tool Name is required';
    } else {
      const toolNameValidation = validateToolName(tool.toolName, props.existingToolNames || [], tool.toolId);
      if (!toolNameValidation.isValid) {
        newErrors.toolName = toolNameValidation.error;
      }
    }
  
    // Validate required fields with null checks
    if (!tool.toolDescription?.trim()) {
      newErrors.toolDescription = 'Tool Description is required';
    }
    
    if (!tool.toolUrlEndpoint?.trim()) {
      newErrors.toolUrlEndpoint = 'Tool URL Endpoint is required';
    }
    
    // Validate authentication if enabled AND either:
    // 1. The field has been touched (new input submitted)
    // 2. The checkbox was toggled (isAuthFieldTouched will be true)
    if (isAuthEnabled && (!tool.toolAuthentication || !tool.toolAuthentication.trim()) && isAuthFieldTouched) {
      newErrors.toolAuthentication = 'Tool Authentication is required when authentication is enabled';
    }
  
    // Validate timeout if provided
    if (!tool.toolTimeout) {
      newErrors.toolTimeout = 'Timeout is required';
    } else {
      const timeout = Number(tool.toolTimeout);
      if (isNaN(timeout) || timeout <= 0 || !Number.isInteger(timeout)) {
        newErrors.toolTimeout = 'Timeout must be a positive whole number';
      }
    }
  
    // Ensure toolParameters exists
    if (!tool.toolParameters) {
      tool.toolParameters = {
        parameters: [],
        testMode: true
      };
    }
  
    // Initialize parameters array if it doesn't exist
    if (!tool.toolParameters.parameters) {
      tool.toolParameters.parameters = [];
    }
  
    // Validate output parameters - Enhanced validation for output field
    if (!tool.toolOutput?.parameters?.output) {
      newErrors.output = 'Output is required';
    } else if (!tool.toolOutput.parameters.output.description?.trim()) {
      newErrors.output = 'Output description is required';
    } else if (!tool.toolOutput.parameters.output.type) {
      newErrors.output = 'Output type is required';
    }

    // Annotation is required for all tools
    if (!tool.toolAnnotation?.trim()) {
      newErrors.toolAnnotation = 'Annotation is required';
    }

    if (!isCoreTool) {
      if (!tool.accessControl.viewAccessType) {
        newErrors.viewAccessType = 'View Access Type is required';
      }
    
      if (!tool.accessControl.editAccessType) {
        newErrors.editAccessType = 'Edit Access Type is required';
      }
    }
  
    setErrors(newErrors);

    // If validation passed, process any pending tags
    if (Object.keys(newErrors).length === 0) {
      // Process pending tags if any
      if (tagManagementRef.current) {
        await tagManagementRef.current.processPendingTags();
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleCoreToolChange = (checked: boolean) => {
    if (readOnly) return;
    setIsCoreTool(checked);
  };

  useImperativeHandle(ref, () => ({
    validateForm,
    getErrors: () => errors
  }));

  const handleInputChange = (field: keyof Tool, value: string | string[] | any) => {
    if (readOnly) return;
  
    if (field === 'toolAuthentication' && !isAuthFieldTouched) {
      setIsAuthFieldTouched(true);
      value = ''; // Clear the field when user starts editing
    }
  
    onToolChange({
      ...tool,
      [field]: value
    });
  
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleInputTouch = (fieldName: string) => {
    setTouchedFields(prev => new Set(prev).add(fieldName));
  };

  const handleParameterAdd = () => {
    if (newParamKey && newParamDesc) {
      onToolChange({
        ...tool,
        toolParameters: {
          ...tool.toolParameters,
          parameters: [
            ...tool.toolParameters.parameters,
            {
              key: newParamKey,
              description: newParamDesc,
              type: newParamType,
              ...(newParamOptions && { options: newParamOptions }),
              ...(newParamExample && { example: newParamExample })
            }
          ]
        }
      });
      setNewParamKey("");
      setNewParamDesc("");
      setNewParamOptions("");
      setNewParamExample("");
      setNewParamType("str");
      setTouchedFields(new Set());
    }
  };

  const handleParameterEdit = (index: number) => {
    const validation = validateParameter(editParamKey, editParamDesc);
    
    if (!validation.isValid) {
      setEditErrors(validation.errors);
      return;
    }

    onToolChange({
      ...tool,
      toolParameters: {
        ...tool.toolParameters,
        parameters: tool.toolParameters.parameters.map((param, i) => 
          i === index ? {
            key: editParamKey,
            description: editParamDesc,
            type: editParamType,
            ...(editParamOptions && { options: editParamOptions }),
            ...(editParamExample && { example: editParamExample })
          } : param
        )
      }
    });
    setEditingParam(null);
    setEditErrors({});
  };

  const handleParameterRemove = (index: number) => {
    setParameterToDelete(index);
  };
  
  const confirmParameterRemove = () => {
    if (parameterToDelete !== null) {
      onToolChange({
        ...tool,
        toolParameters: {
          ...tool.toolParameters,
          parameters: tool.toolParameters.parameters.filter((_, i) => i !== parameterToDelete)
        }
      });
      setParameterToDelete(null);
    }
  };

  const validateParameter = (key: string, description: string) => {
    const errors: { key?: string; description?: string } = {};
    
    if (!key.trim()) {
      errors.key = 'Parameter key is required';
    }
    if (!description.trim()) {
      errors.description = 'Description is required';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  const hasEditAccess = (accessControl: AccessControl) => {
    const currentUserId = user?.sub;
    const isOwner = tool.ownerId === currentUserId;
    
    if (!currentUserId) return false;
    
    // Admins always have edit access
    if (isCurrentUserAdmin) return true;
    
    // Owners always have edit access to their own tools
    if (isOwner) return true;
    
    // For non-admin, non-owner users, check access control settings
    const { editAccessType, editAllowedUsers } = accessControl;
    
    switch (editAccessType) {
      case 'All Users':
        return true;
      case 'Allowed Users':
        return editAllowedUsers.includes(currentUserId);
      case 'Owner Only':
        return false; // Already checked for owner above
      default:
        return false;
    }
  };

  const AccessControlViewSection = () => {
    const [ownerDetails, setOwnerDetails] = useState({ email: '', name: '' });
    
    useEffect(() => {
      const fetchOwnerDetails = async () => {
        try {
          const usersData = await getUsers();
          const owner = usersData.find((user) => user.userId === tool.ownerId);
          if (owner) {
            setOwnerDetails({
              email: owner.email,
              name: owner.email //TODO: Add name to user table
            });
          }
        } catch (error) {
          console.error('Error fetching owner details:', error);
        }
      };
      
      fetchOwnerDetails();
    }, [tool.ownerId]);

    // For Core Tools, show a special message
    if (tool.isCore) {
      return (
        <ViewModeField
          label="Access Control"
          value={
            <div className="flex items-center space-x-2 text-gray-700">
              <Info className="h-5 w-5 text-blue-500" />
              <div>
                <p className="font-medium">Administrator Only</p>
                <p className="text-sm text-gray-600">
                  This Core Tool is managed by administrators only.
                </p>
              </div>
            </div>
          }
        />
      );
    }
    
  
    return (
      <ViewModeField
        label="Access Control"
        value={
      <AccessDisplay
        viewAccessType={tool.accessControl.viewAccessType}
        editAccessType={tool.accessControl.editAccessType}
        viewAllowedUsers={tool.accessControl.viewAccessType === 'Allowed Users' 
          ? tool.accessControl.viewAllowedUsers 
          : []}
        editAllowedUsers={tool.accessControl.editAccessType === 'Allowed Users' 
          ? tool.accessControl.editAllowedUsers 
          : []}
        ownerEmail={ownerDetails.email}
        ownerName={ownerDetails.name}
      />
        }
      />
    );
  };

  const renderViewMode = () => (
    <Card className="max-w-3xl mx-auto">
      <CardHeader className="flex flex-col gap-4 pb-2">
  <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full">
    <CardTitle className="text-2xl break-words max-w-full overflow-hidden text-ellipsis">
      {tool.toolName}
    </CardTitle>
    <div className="flex gap-2 mt-2 sm:mt-0 flex-shrink-0">
      <Button
        variant="outline"
        onClick={() => navigate(`/studio/tools/test/${tool.toolId}`)}
        className="border-green-500 text-green-500 hover:bg-green-50 hover:text-green-600"
      >
        <PlayCircle className="mr-2 h-4 w-4" />
        Test
      </Button>
      {hasEditAccess(tool.accessControl) && (
        <Button
          variant="outline"
          onClick={onEditClick}
          className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
        >
          <Pencil className="mr-2 h-4 w-4" />
          Edit
        </Button>
      )}
      <Button
        variant="outline"
        onClick={onCancel}
        className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Tools
      </Button>
    </div>
  </div>
</CardHeader>

      <CardContent className="space-y-6">
  <ViewModeField 
    label="Description" 
    value={tool.toolDescription} 
  />
  
  <ViewModeField 
    label="URL Endpoint" 
    value={tool.toolUrlEndpoint} 
  />
  
  <ViewModeField 
    label="Authentication" 
    value={tool.isAuthPresent ? "••••••••" : "No authentication"} 
  />
  
  <ViewModeField 
    label="Timeout (seconds)" 
    value={tool.toolTimeout || DEFAULT_TOOL_TIMEOUT} 
  />
  
  <ViewModeField 
    label="Tags" 
    value={
      <TagManagement
        tags={tool.toolTags || []}
        onChange={() => {}}
        readOnly={true}
        suggestions={tagSuggestions}
      />
    }
  />

  <ViewModeField
    label="Input Parameters"
    value={
      <Card className="bg-gray-50 border-none shadow-none p-4">
        <ViewModeParameters parameters={tool.toolParameters?.parameters ?? []}  />
      </Card>
    }
  />

  <ViewModeField
    label="Output"
    value={
      <Card className="bg-gray-50 border-none shadow-none p-4">
        <ViewModeOutput output={tool.toolOutput} />
      </Card>
    }
  />

  <ViewModeField
    label="Annotation"
    value={
      <Card className="bg-gray-50 border-none shadow-none p-4">
        <ViewModeAnnotation annotation={tool.toolAnnotation} />
      </Card>
    }
  />

<AccessControlViewSection />

</CardContent>
    </Card>
  );

  const renderAuthenticationField = () => (
    <FormField
      helpTitle="Tool Authentication Help"
      label={
        <div className="flex items-center justify-between w-full">
          <div className="flex-grow">Tool Authentication {isAuthEnabled && <span className="text-red-500">*</span>}</div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <Checkbox 
              checked={isAuthEnabled}
              onCheckedChange={(checked: boolean) => {
                setIsAuthEnabled(checked);
                if (!checked) {
                  handleInputChange('toolAuthentication', '');
                  setIsAuthFieldTouched(false);
                } else {
                  // When enabling auth, mark the field as touched to trigger validation
                  setIsAuthFieldTouched(true);
                }
              }}
            />
          </div>
        </div>
      }
      helpText={formatHelpText(toolFormHelpText.toolAuthentication)}
      error={errors.toolAuthentication}
    >
      {!isAuthEnabled ? (
        <p className="text-sm text-gray-500 mt-1">
          {/* Authentication is disabled for this tool */}
        </p>
      ) : (
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="text"
                value={isAuthFieldTouched ? tool.toolAuthentication : '••••••••'}
                onChange={(e) => handleInputChange('toolAuthentication', e.target.value)}
                placeholder="Enter new authentication"
                className={errors.toolAuthentication ? 'border-red-500' : ''}
                onFocus={() => {
                  if (!isAuthFieldTouched) {
                    handleInputChange('toolAuthentication', '');
                  }
                }}
                required={isAuthEnabled}
              />
            </div>
          </div>
          {(!tool.toolUrlEndpoint) && (
            <p className="text-sm text-gray-500 mt-1">
              {/* Please enter a URL endpoint before testing authentication */}
            </p>
          )}
        </div>
      )}
    </FormField>
  );
  

  const renderTagManagementField = () => (
    <FormField
      label="Tool Tags"
      helpText={formatHelpText(toolFormHelpText.toolTags)}
    >
      <TagManagement
        ref={tagManagementRef}
        tags={tool.toolTags || []}
        onChange={(newTags) => {
          onToolChange({
            ...tool,
            toolTags: newTags
          });
        }}
        readOnly={false}
        suggestions={tagSuggestions}
        className="mt-1"
      />
      <p className="text-sm text-muted-foreground mt-1">
        {/* Search existing tags or type to add new ones */}
      </p>
    </FormField>
  );

const renderEditMode = () => {
  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Basic Information';
      case 2: return 'Configuration';
      case 3: return 'Input Parameters';
      case 4: return 'Output Definition';
      case 5: return 'Annotation';
      case 6: return 'Access Control';
      default: return 'Create Tool';
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 1: return 'Define your tool\'s identity and purpose';
      case 2: return 'Set up your tool\'s connection and settings';
      case 3: return 'Define input parameters (optional)';
      case 4: return 'Specify what your tool returns';
      case 5: return 'Provide detailed usage instructions';
      case 6: return 'Set permissions and access levels';
      default: return 'Build a custom tool to extend your agents';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 font-['Poppins','SF_Pro_Display','system-ui','-apple-system','sans-serif'] antialiased">
      
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-50 shadow-sm">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 sm:gap-6">
              <Button
                variant="ghost"
                onClick={onCancel}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-100/80 p-2 sm:p-3 rounded-xl transition-all duration-300 flex-shrink-0"
              >
                <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6" />
              </Button>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight font-['Radley','serif'] mb-1">
                  {getStepTitle()}
                </h1>
                <p className="text-gray-600 text-sm sm:text-base">
                  {getStepDescription()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        
        {/* Progress Indicator */}
        <div className="max-w-6xl mx-auto mb-8 sm:mb-12">
          <div className="flex items-center justify-between pb-2 sm:pb-0">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => {
              const getStepLabel = (stepNum: number) => {
                switch (stepNum) {
                  case 1: return 'Basic Info';
                  case 2: return 'Configuration';
                  case 3: return 'Parameters';
                  case 4: return 'Output';
                  case 5: return 'Annotation';
                  case 6: return 'Access Control';
                  default: return `Step ${stepNum}`;
                }
              };

              return (
                <div key={step} className="flex items-center flex-shrink-0">
                  <button
                    onClick={() => handleStepClick(step)}
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold text-xs sm:text-sm transition-all duration-300 ${
                      step === currentStep
                        ? 'bg-blue-600 text-white shadow-lg scale-110'
                        : completedSteps.has(step)
                        ? 'bg-green-500 text-white cursor-pointer hover:bg-green-600'
                        : step < currentStep
                        ? 'bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-200'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                    disabled={step > currentStep && !completedSteps.has(step - 1)}
                  >
                    {completedSteps.has(step) ? '✓' : step}
                  </button>
                  <span className={`ml-2 text-xs sm:text-sm font-medium whitespace-nowrap ${
                    step === currentStep
                      ? 'text-blue-600'
                      : completedSteps.has(step)
                      ? 'text-green-600'
                      : 'text-gray-500'
                  }`}>
                    {getStepLabel(step)}
                  </span>
                  {step < totalSteps && (
                    <div className={`w-8 sm:w-12 h-1 mx-2 sm:mx-3 rounded-full ${
                      completedSteps.has(step) ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="max-w-2xl mx-auto">
          <Card className="border-0 shadow-2xl bg-white">
            <CardContent className="p-6 sm:p-8 lg:p-10">
              
              {/* Step 1: Basic Information */}
              {currentStep === 1 && (
                <div className="space-y-6 sm:space-y-8">
                  {/* Tool Name */}
                  <div>
                    <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      Tool Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={tool.toolName}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        handleInputChange('toolName', newValue);
                        
                        const validation = validateToolName(newValue, props.existingToolNames || [], tool.toolId);
                        if (!validation.isValid) {
                          setErrors(prev => ({
                            ...prev,
                            toolName: validation.error
                          }));
                        } else {
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.toolName;
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="e.g., Weather API, Email Sender, Data Processor"
                      className={`rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-300 text-sm sm:text-base py-3 sm:py-4 px-4 sm:px-6 shadow-sm hover:shadow-md ${errors.toolName ? 'border-red-300 focus:border-red-500' : ''}`}
                    />
                    {errors.toolName && (
                      <p className="mt-2 sm:mt-3 text-red-500 text-sm font-medium">{errors.toolName}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      value={tool.toolDescription}
                      onChange={(e) => handleInputChange('toolDescription', e.target.value)}
                      placeholder="Describe what this tool does and how it helps your agents accomplish their tasks"
                      rows={4}
                      className={`rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-300 resize-none text-sm sm:text-base py-3 sm:py-4 px-4 sm:px-6 shadow-sm hover:shadow-md ${errors.toolDescription ? 'border-red-300 focus:border-red-500' : ''}`}
                    />
                    {errors.toolDescription && (
                      <p className="mt-2 sm:mt-3 text-red-500 text-sm font-medium">{errors.toolDescription}</p>
                    )}
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      Tags <span className="text-gray-500 text-sm sm:text-base font-normal">(Optional)</span>
                    </label>
                    {renderTagManagementField()}
                  </div>
                </div>
              )}

              {/* Step 2: Configuration */}
              {currentStep === 2 && (
                <div className="space-y-6 sm:space-y-8">
                  {/* API Endpoint */}
                  <div>
                    <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      API Endpoint <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={tool.toolUrlEndpoint}
                      onChange={(e) => handleInputChange('toolUrlEndpoint', e.target.value)}
                      placeholder="https://api.example.com/v1/endpoint"
                      className={`rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500/20 transition-all duration-300 font-mono text-sm sm:text-base py-3 sm:py-4 px-4 sm:px-6 shadow-sm hover:shadow-md ${errors.toolUrlEndpoint ? 'border-red-300 focus:border-red-500' : ''}`}
                    />
                    {errors.toolUrlEndpoint && (
                      <p className="mt-2 sm:mt-3 text-red-500 text-sm font-medium">{errors.toolUrlEndpoint}</p>
                    )}
                  </div>

                  {/* Authentication */}
                  <div>
                    <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      Authentication <span className="text-gray-500 text-sm sm:text-base font-normal">(Optional)</span>
                    </label>
                    {renderAuthenticationField()}
                  </div>

                  {/* Timeout */}
                  <div>
                    <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      Timeout <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={tool.toolTimeout || DEFAULT_TOOL_TIMEOUT}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (!value || (parseInt(value) > 0 && Number.isInteger(parseFloat(value)))) {
                            handleInputChange('toolTimeout', value);
                          }
                        }}
                        placeholder="30"
                        className={`rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500/20 transition-all duration-300 text-sm sm:text-base py-3 sm:py-4 px-4 sm:px-6 pr-16 sm:pr-20 shadow-sm hover:shadow-md ${errors.toolTimeout ? 'border-red-300 focus:border-red-500' : ''}`}
                      />
                      <span className="absolute right-4 sm:right-6 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium text-sm sm:text-base">seconds</span>
                    </div>
                    {errors.toolTimeout && (
                      <p className="mt-2 sm:mt-3 text-red-500 text-sm font-medium">{errors.toolTimeout}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: Input Parameters */}
              {currentStep === 3 && (
                <div className="space-y-6 sm:space-y-8">
                  {/* Parameters List */}
                  {tool.toolParameters.parameters.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                      <div className="bg-gradient-to-br from-violet-100 to-violet-200 p-6 sm:p-8 rounded-full w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 sm:mb-8 flex items-center justify-center">
                        <Plus className="h-8 w-8 sm:h-10 sm:w-10 text-violet-600" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">No parameters yet</h3>
                      <p className="text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base leading-relaxed max-w-sm mx-auto">
                        Add input parameters to define what data your tool needs to function properly
                      </p>
                      <Button
                        type="button"
                        onClick={() => setIsAddingParameter(true)}
                        className="bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-sm sm:text-base"
                      >
                        <Plus className="mr-2 sm:mr-3 h-5 w-5 sm:h-6 sm:w-6" />
                        Add First Parameter
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4 sm:space-y-6">
                      {tool.toolParameters.parameters.map((param, index) => (
                        <div key={index} className="bg-gradient-to-r from-violet-50 to-violet-100 border border-violet-200 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all duration-300">
                          <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
                                <span className="font-bold text-gray-900 font-mono text-base break-all">{param.key}</span>
                                <span className="px-3 py-1 bg-violet-600 text-white text-xs rounded-full font-semibold self-start">
                                  {param.type}
                                </span>
                              </div>
                              <p className="text-gray-700 text-sm mb-3 leading-relaxed">{param.description}</p>
                              {(param.options || param.example) && (
                                <div className="space-y-2 text-xs text-gray-600">
                                  {param.options && (
                                    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                      <span className="font-semibold text-violet-700 flex-shrink-0">Options:</span>
                                      <span className="break-words">{param.options}</span>
                                    </div>
                                  )}
                                  {param.example && (
                                    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                      <span className="font-semibold text-violet-700 flex-shrink-0">Example:</span>
                                      <code className="bg-white px-2 py-1 rounded-lg border text-xs break-all">{param.example}</code>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 self-start sm:self-center">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleParameterEdit(index)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg p-2"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleParameterRemove(index)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg p-2"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsAddingParameter(true)}
                        className="w-full border-2 border-dashed border-violet-300 text-violet-600 hover:bg-violet-50 hover:border-violet-400 rounded-xl py-4 text-base font-semibold transition-all duration-300"
                      >
                        <Plus className="mr-2 h-5 w-5" />
                        Add Another Parameter
                      </Button>
                    </div>
                  )}

                  {/* Add Parameter Form */}
                  {isAddingParameter && (
                    <div className="mt-6 space-y-6 bg-gradient-to-br from-violet-50 to-violet-100 p-4 sm:p-6 rounded-2xl border-2 border-violet-200">
                      <h4 className="text-lg font-bold text-gray-900 mb-4">Add New Parameter</h4>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Name <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={newParamKey}
                            onChange={(e) => setNewParamKey(e.target.value)}
                            onBlur={() => handleInputTouch('paramKey')}
                            placeholder="parameter_name"
                            className={`rounded-lg border-gray-200 focus:border-violet-500 focus:ring-violet-500/20 font-mono text-sm py-2 px-3 ${touchedFields.has('paramKey') && !newParamKey ? 'border-red-300' : ''}`}
                          />
                          {touchedFields.has('paramKey') && !newParamKey && 
                            <p className="mt-2 text-sm text-red-500 font-medium">Parameter name is required</p>
                          }
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Type <span className="text-red-500">*</span>
                          </label>
                          <Select value={newParamType} onValueChange={setNewParamType}>
                            <SelectTrigger className="rounded-lg border-gray-200 focus:border-violet-500 focus:ring-violet-500/20 py-2 text-sm">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="str">String</SelectItem>
                              <SelectItem value="int">Integer</SelectItem>
                              <SelectItem value="float">Float</SelectItem>
                              <SelectItem value="bool">Boolean</SelectItem>
                              <SelectItem value="list">List</SelectItem>
                              <SelectItem value="dict">Dictionary</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <Textarea
                          value={newParamDesc}
                          onChange={(e) => setNewParamDesc(e.target.value)}
                          onBlur={() => handleInputTouch('paramDesc')}
                          placeholder="Describe what this parameter does and how it's used"
                          rows={3}
                          className={`rounded-lg border-gray-200 focus:border-violet-500 focus:ring-violet-500/20 resize-none text-sm py-2 px-3 ${touchedFields.has('paramDesc') && !newParamDesc ? 'border-red-300' : ''}`}
                        />
                        {touchedFields.has('paramDesc') && !newParamDesc && 
                          <p className="mt-2 text-sm text-red-500 font-medium">Description is required</p>
                        }
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Options <span className="text-gray-500 text-xs font-normal">(Optional)</span>
                          </label>
                          <Input
                            value={newParamOptions}
                            onChange={(e) => setNewParamOptions(e.target.value)}
                            placeholder="option1, option2, option3"
                            className="rounded-lg border-gray-200 focus:border-violet-500 focus:ring-violet-500/20 text-sm py-2 px-3"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Example <span className="text-gray-500 text-xs font-normal">(Optional)</span>
                          </label>
                          <Input
                            value={newParamExample}
                            onChange={(e) => setNewParamExample(e.target.value)}
                            placeholder="sample_value"
                            className="rounded-lg border-gray-200 focus:border-violet-500 focus:ring-violet-500/20 font-mono text-sm py-2 px-3"
                          />
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-violet-200">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setIsAddingParameter(false);
                            setNewParamKey('');
                            setNewParamDesc('');
                            setNewParamType('str');
                            setNewParamOptions('');
                            setNewParamExample('');
                            setTouchedFields(new Set());
                          }}
                          className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg px-6 py-2 font-semibold text-sm order-2 sm:order-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={handleParameterAdd}
                          className="bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800 text-white rounded-lg px-6 py-2 font-semibold text-sm order-1 sm:order-2"
                        >
                          Add Parameter
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Output Definition */}
              {currentStep === 4 && (
                <div className="space-y-6 sm:space-y-8">
                  <div>
                    <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      Output Description <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      value={tool.toolOutput?.parameters?.output?.description || ''}
                      onChange={(e) => {
                        const newOutput = {
                          ...tool.toolOutput,
                          parameters: {
                            ...tool.toolOutput?.parameters,
                            output: {
                              ...tool.toolOutput?.parameters?.output,
                              description: e.target.value
                            }
                          }
                        };
                        handleInputChange('toolOutput', newOutput);
                      }}
                      placeholder="Describe what this tool returns and how the output should be interpreted"
                      rows={4}
                      className={`rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 transition-all duration-300 resize-none text-sm sm:text-base py-3 sm:py-4 px-4 sm:px-6 shadow-sm hover:shadow-md ${errors.output ? 'border-red-300 focus:border-red-500' : ''}`}
                    />
                    {errors.output && (
                      <p className="mt-2 sm:mt-3 text-red-500 text-sm font-medium">{errors.output}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      Output Type <span className="text-red-500">*</span>
                    </label>
                    <Select 
                      value={tool.toolOutput?.parameters?.output?.type || ''} 
                      onValueChange={(value) => {
                        const newOutput = {
                          ...tool.toolOutput,
                          parameters: {
                            ...tool.toolOutput?.parameters,
                            output: {
                              ...tool.toolOutput?.parameters?.output,
                              type: value
                            }
                          }
                        };
                        handleInputChange('toolOutput', newOutput);
                      }}
                    >
                      <SelectTrigger className="rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 py-3 sm:py-4 text-sm sm:text-base shadow-sm hover:shadow-md">
                        <SelectValue placeholder="Select output type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="str">String</SelectItem>
                        <SelectItem value="int">Integer</SelectItem>
                        <SelectItem value="float">Float</SelectItem>
                        <SelectItem value="bool">Boolean</SelectItem>
                        <SelectItem value="list">List</SelectItem>
                        <SelectItem value="dict">Dictionary</SelectItem>
                        <SelectItem value="json">JSON Object</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      Example Output <span className="text-gray-500 text-sm sm:text-base font-normal">(Optional)</span>
                    </label>
                    <Textarea
                      value={(tool.toolOutput?.parameters?.output as any)?.example || ''}
                      onChange={(e) => {
                        const currentOutput = tool.toolOutput?.parameters?.output as any || {};
                        const newOutput = {
                          ...tool.toolOutput,
                          parameters: {
                            ...tool.toolOutput?.parameters,
                            output: {
                              ...currentOutput,
                              example: e.target.value
                            }
                          }
                        };
                        handleInputChange('toolOutput', newOutput);
                      }}
                      placeholder="Provide an example of what this tool returns"
                      rows={3}
                      className="rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 transition-all duration-300 resize-none font-mono text-sm sm:text-base py-3 sm:py-4 px-4 sm:px-6 shadow-sm hover:shadow-md"
                    />
                  </div>
                </div>
              )}

              {/* Step 5: Annotation */}
              {currentStep === 5 && (
                <div className="space-y-6 sm:space-y-8">
                  <div>
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <label className="block text-base sm:text-lg font-semibold text-gray-900">
                        Tool Annotation <span className="text-red-500">*</span>
                      </label>
                      <Button
                        type="button"
                        onClick={generateAnnotation}
                        disabled={isGeneratingAnnotation || !tool.toolName || !tool.toolDescription || !tool.toolUrlEndpoint}
                        className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isGeneratingAnnotation ? (
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Generating...
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4" />
                            Generate with AI
                          </div>
                        )}
                      </Button>
                    </div>
                    <p className="text-gray-600 text-sm sm:text-base mb-4 leading-relaxed">
                      Provide detailed instructions on how to use this tool effectively. This helps agents understand when and how to use your tool.
                    </p>
                    <Textarea
                      value={tool.toolAnnotation || ''}
                      onChange={(e) => handleInputChange('toolAnnotation', e.target.value)}
                      placeholder="Explain how to use this tool, what parameters to provide, and what to expect from the output..."
                      rows={8}
                      className={`rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 transition-all duration-300 resize-none text-sm sm:text-base py-3 sm:py-4 px-4 sm:px-6 shadow-sm hover:shadow-md ${errors.toolAnnotation ? 'border-red-300 focus:border-red-500' : ''}`}
                    />
                    {errors.toolAnnotation && (
                      <p className="mt-2 sm:mt-3 text-red-500 text-sm font-medium">{errors.toolAnnotation}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Step 6: Access Control (only for non-core tools) */}
              {currentStep === 6 && !isCoreTool && (
                <div className="space-y-6 sm:space-y-8">
                  <div>
                    <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      View Access Type <span className="text-red-500">*</span>
                    </label>
                    <Select 
                      value={tool.accessControl?.viewAccessType || ''} 
                      onValueChange={(value) => {
                        const newAccessControl = {
                          ...tool.accessControl,
                          viewAccessType: value
                        };
                        handleInputChange('accessControl', newAccessControl);
                      }}
                    >
                      <SelectTrigger className={`rounded-xl border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20 py-3 sm:py-4 text-sm sm:text-base shadow-sm hover:shadow-md ${errors.viewAccessType ? 'border-red-300 focus:border-red-500' : ''}`}>
                        <SelectValue placeholder="Select view access type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Public">Public</SelectItem>
                        <SelectItem value="Owner Only">Owner Only</SelectItem>
                        <SelectItem value="Allowed Users">Allowed Users</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.viewAccessType && (
                      <p className="mt-2 sm:mt-3 text-red-500 text-sm font-medium">{errors.viewAccessType}</p>
                    )}
                  </div>

                  {/* View Access Users Selection */}
                  {tool.accessControl?.viewAccessType === 'Allowed Users' && (
                    <div>
                      <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                        Select Users for View Access
                      </label>
                      {isLoadingUsers ? (
                        <div className="flex items-center gap-2 text-gray-500">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading users...
                        </div>
                      ) : (
                        <MultiSelect
                          options={users.map(user => ({
                            value: user.userId,
                            label: user.email,
                            tags: [user.email, user.userId]
                          }))}
                          value={tool.accessControl?.viewAllowedUsers || []}
                          onChange={(selectedUsers) => {
                            const newAccessControl = {
                              ...tool.accessControl,
                              viewAllowedUsers: selectedUsers
                            };
                            handleInputChange('accessControl', newAccessControl);
                          }}
                          placeholder="Search and select users..."
                          className="rounded-xl border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20"
                        />
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      Edit Access Type <span className="text-red-500">*</span>
                    </label>
                    <Select 
                      value={tool.accessControl?.editAccessType || ''} 
                      onValueChange={(value) => {
                        const newAccessControl = {
                          ...tool.accessControl,
                          editAccessType: value
                        };
                        handleInputChange('accessControl', newAccessControl);
                      }}
                    >
                      <SelectTrigger className={`rounded-xl border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20 py-3 sm:py-4 text-sm sm:text-base shadow-sm hover:shadow-md ${errors.editAccessType ? 'border-red-300 focus:border-red-500' : ''}`}>
                        <SelectValue placeholder="Select edit access type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Owner Only">Owner Only</SelectItem>
                        <SelectItem value="Allowed Users">Allowed Users</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.editAccessType && (
                      <p className="mt-2 sm:mt-3 text-red-500 text-sm font-medium">{errors.editAccessType}</p>
                    )}
                  </div>

                  {/* Edit Access Users Selection */}
                  {tool.accessControl?.editAccessType === 'Allowed Users' && (
                    <div>
                      <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                        Select Users for Edit Access
                      </label>
                      {isLoadingUsers ? (
                        <div className="flex items-center gap-2 text-gray-500">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading users...
                        </div>
                      ) : (
                        <MultiSelect
                          options={users.map(user => ({
                            value: user.userId,
                            label: user.email,
                            tags: [user.email, user.userId]
                          }))}
                          value={tool.accessControl?.editAllowedUsers || []}
                          onChange={(selectedUsers) => {
                            const newAccessControl = {
                              ...tool.accessControl,
                              editAllowedUsers: selectedUsers
                            };
                            handleInputChange('accessControl', newAccessControl);
                          }}
                          placeholder="Search and select users..."
                          className="rounded-xl border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20"
                        />
                      )}
                    </div>
                  )}

                  {/* Core Tool Toggle for Admin Users */}
                  {isCurrentUserAdmin && (
                    <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-4 sm:p-6">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <input
                          type="checkbox"
                          id="coreToolToggle"
                          checked={isCoreTool}
                          onChange={(e) => handleCoreToolChange(e.target.checked)}
                          className="w-5 h-5 text-amber-600 bg-white border-amber-300 rounded focus:ring-amber-500 focus:ring-2"
                        />
                        <div>
                          <label htmlFor="coreToolToggle" className="text-base sm:text-lg font-semibold text-gray-900 cursor-pointer">
                            Core Tool
                          </label>
                          <p className="text-gray-600 text-sm sm:text-base mt-1">
                            Mark this as a core system tool (admin only)
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center pt-8 mt-8 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePreviousStep}
                  disabled={currentStep === 1}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl px-6 py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Previous
                </Button>
                
                {currentStep < totalSteps ? (
                  <Button
                    type="button"
                    onClick={handleNextStep}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 px-8 py-3 rounded-xl font-semibold"
                  >
                    Next Step
                    <ArrowLeft className="ml-2 h-4 w-4 rotate-180" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={onSubmit}
                    disabled={props.isSaving}
                    className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 px-8 py-3 rounded-xl font-semibold"
                  >
                    {props.isSaving ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <PlayCircle className="h-4 w-4" />
                        Save & Test
                      </div>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};


  return (
    <div className="w-full h-full">
      {isGeneratingAnnotation && <LoadingOverlay />}
      {props.isSaving && <SaveOverlay />}
      {readOnly ? renderViewMode() : renderEditMode()}
      <ConfirmationDialog
        open={parameterToDelete !== null}
        onOpenChange={(open) => !open && setParameterToDelete(null)}
        onConfirm={confirmParameterRemove}
        title="Remove Parameter"
        description="Are you sure you want to remove this parameter? This action cannot be undone."
        confirmLabel="Remove"
        cancelLabel="Cancel"
      />
    </div>
  );
});

export default ToolForm;